import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, HeartPulseIcon, TriangleAlertIcon } from 'lucide-react';
import { AdminPageTitle, AdminSearch, AdminTabs, ConfirmationModal, FilterBar, FilterSelect, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { HeartGlyph } from '../../components/icons/BeelyvIcons';
import { Button } from '../../components/ui/Button';
import { campaigns, missingContentFields, type AdminCampaign, type CampaignContent, type CampaignLocale, type CampaignType, type LocalizedCampaignText } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
const typeLabels: Record<CampaignType, string> = {
  operational: 'Operacional',
  health: 'Lembrete de saúde',
  product: 'Novidade de produto',
  campaign: 'Campanha',
  system: 'Sistema'
};

/** Type label as it appears to en-US users in the in-app preview. */
const typeLabelsEn: Record<CampaignType, string> = {
  operational: 'Operational',
  health: 'Health reminder',
  product: 'Product update',
  campaign: 'Campaign',
  system: 'System'
};
const statusLabels: Record<string, string> = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  sent: 'Enviada'
};
export function AdminNotifications() {
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return campaigns.filter(item => {
      if (tab !== 'all' && item.status !== tab) return false;
      if (type !== 'all' && item.type !== type) return false;
      if (!term) return true;
      return item.title.toLowerCase().includes(term);
    });
  }, [tab, query, type]);
  const columns: Column<AdminCampaign>[] = [{
    id: 'title',
    header: 'Comunicação',
    width: '30%',
    sortValue: row => row.title,
    cell: row => <span className="flex min-w-0 items-start gap-2" data-id="element-1831" data-mp="1lbynuc577r1d">
          <span className={`mt-[2px] grid h-8 w-8 shrink-0 place-items-center rounded-[10px] ${row.type === 'health' ? 'bg-gold/20 text-gold-deep' : 'bg-sand-deep text-muted'}`} aria-hidden="true" data-id="element-1832" data-mp="1ladswlez262z">
            {row.type === 'health' ? <HeartGlyph size={15} data-id="element-1833" data-mp="xo79z440f5xz" /> : <BellIcon size={15} data-id="element-1834" data-mp="xkt6ttniusph" />}
          </span>
          <span className="min-w-0" data-id="element-1835" data-mp="12ms9f1bftlld">
            <span className="block text-[12.5px] font-medium leading-snug text-deep" data-id="element-1836" data-mp="12jmlhpejopr8">
              {row.title}
            </span>
            <span className="block text-[11px] text-muted" data-id="element-1837" data-mp="1cba89d05pimd">{typeLabels[row.type]}</span>
          </span>
        </span>
  }, {
    id: 'audience',
    header: 'Audiência',
    width: '22%',
    cell: row => row.healthSegment ? <span className="min-w-0" data-id="element-1838" data-mp="jt4ubp9d227z">
            <span className="block text-[12px] text-deep" data-id="element-1839" data-mp="2x592uo31ezif">{tl(row.audience)}</span>
            <span className="block text-[11px] text-muted" data-id="element-1840" data-mp="2t3xiawz0t4h3">
              Segmento automático de saúde · {row.healthSegment.estimate} estimados
            </span>
            <span className="block text-[11px] text-muted" data-id="element-1841" data-mp="2o39hjjkzp99o">{tl(row.healthSegment.eligibility)}</span>
          </span> : <span className="text-[12px] text-muted" data-id="element-1842" data-mp="2j83zwwymegre">{tl(row.audience)}</span>
  }, {
    id: 'languages',
    header: 'Idiomas',
    cell: row => <span className="bee-num text-[11.5px] text-muted" data-id="element-1843" data-mp="29f4yuflxsxqw">{row.languages}</span>
  }, {
    id: 'when',
    header: 'Data',
    sortValue: row => row.when,
    cell: row => <span className="bee-num text-muted" data-id="element-1844" data-mp="20gb28p8lw7dc">{row.when}</span>
  }, {
    id: 'reach',
    header: 'Alcance',
    align: 'right',
    cell: row => <span className="bee-num text-deep" data-id="element-1845" data-mp="1qh2dcasllixu">{row.reach ?? '—'}</span>,
    optional: true
  }, {
    id: 'status',
    header: 'Status',
    sortValue: row => row.status,
    cell: row => <StatusBadge status={row.status} label={statusLabels[row.status]} data-id="element-1846" data-mp="3vieubof2q1re" />
  }];
  return <div className="space-y-4" data-id="element-1847" data-mp="3vk97uho0mrlg">
      <AdminPageTitle title="Notificações" subtitle="Comunicações da plataforma, por tipo, audiência e idioma." action={<Button variant="gold" onClick={() => navigate('/admin/notificacoes/nova')} data-id="element-1849" data-mp="3lqiizbnz8f58">
            Nova comunicação
          </Button>} data-id="element-1848" data-mp="3guaagh4abk71" />

      <AdminTabs tabs={[{
      id: 'all',
      label: 'Todas'
    }, {
      id: 'draft',
      label: 'Rascunhos',
      count: campaigns.filter(c => c.status === 'draft').length
    }, {
      id: 'scheduled',
      label: 'Agendadas',
      count: campaigns.filter(c => c.status === 'scheduled').length
    }, {
      id: 'sent',
      label: 'Enviadas',
      count: campaigns.filter(c => c.status === 'sent').length
    }]} active={tab} onChange={setTab} data-id="element-1850" data-mp="3cs6vvjmbjzhj" />

      <FilterBar data-id="element-1851" data-mp="2lket0wlry17z">
        <AdminSearch value={query} onChange={setQuery} placeholder="Buscar comunicação" data-id="element-1852" data-mp="2ljvmec1kqz99" />
        <FilterSelect label="Tipo" value={type} onChange={setType} options={[{
        id: 'all',
        label: 'Todos'
      }, {
        id: 'operational',
        label: 'Operacional'
      }, {
        id: 'health',
        label: 'Saúde'
      }, {
        id: 'product',
        label: 'Produto'
      }, {
        id: 'campaign',
        label: 'Campanha'
      }, {
        id: 'system',
        label: 'Sistema'
      }]} data-id="element-1853" data-mp="2gllq1y445sm2" />
      </FilterBar>

      <DataTable rows={rows} columns={columns} rowKey={row => row.id} emptyTitle="Nenhuma comunicação" emptyBody="Crie uma nova comunicação ou ajuste os filtros." actions={[{
      id: 'edit',
      label: 'Editar',
      onSelect: () => navigate('/admin/notificacoes/nova')
    }, {
      id: 'duplicate',
      label: 'Duplicar',
      onSelect: () => navigate('/admin/notificacoes/nova')
    }]} data-id="element-1854" data-mp="308rto6vt488t" />

      <Panel title="Regra de uso" data-id="element-1855" data-mp="3eyhhei1e57bm">
        <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted" data-id="element-1856" data-mp="3ezj3rinuxg8n">
          <TriangleAlertIcon size={15} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-1857" data-mp="3jyc7wkh97uw5" />
          Comunicações de campanha e de produto não podem usar o formato de lembrete de
          saúde. O tipo escolhido define o canal, a prioridade e o rótulo exibido ao
          usuário — marketing nunca se apresenta como alerta de saúde.
        </p>
      </Panel>
    </div>;
}
export function AdminCreateNotification() {
  const navigate = useNavigate();
  const [type, setType] = useState<CampaignType>('health');
  // Neutral by default: never tells a user the streak will be lost, since they may
  // still hold an available Streak Protection.
  // Title and message are stored separately per locale (see CampaignContent).
  const [content, setContent] = useState<CampaignContent>({
    'pt-BR': {
      title: 'Seu Daily Check ainda não fechou',
      message: 'Faltam 25 Bee para fechar o dia. Veja o que ainda falta no seu Daily Check.'
    },
    'en-US': {
      title: "Your Daily Check isn't closed yet",
      message: '25 Bee left to close the day. See what is still open in your Daily Check.'
    }
  });
  const [attempted, setAttempted] = useState(false);
  /** Preview-only language switch. Reads the live per-locale fields. */
  const [previewLocale, setPreviewLocale] = useState<CampaignLocale>('pt-BR');
  const [audience, setAudience] = useState('nocheck');
  const [schedule, setSchedule] = useState('22/09/2026 21:30');
  const [confirm, setConfirm] = useState(false);
  const isHealth = type === 'health';
  const missing = missingContentFields(content);
  const canSchedule = missing.length === 0;
  const update = (locale: CampaignLocale, field: keyof LocalizedCampaignText, value: string) => setContent(prev => ({
    ...prev,
    [locale]: {
      ...prev[locale],
      [field]: value
    }
  }));
  const requestSchedule = () => {
    setAttempted(true);
    if (canSchedule) setConfirm(true);
  };
  const fieldError = (locale: CampaignLocale, field: keyof LocalizedCampaignText) => attempted && !content[locale][field].trim();
  const fieldClass = (invalid: boolean, textarea = false) => `mt-1 w-full rounded-card border bg-white text-[13px] text-deep focus:outline-none ${textarea ? 'p-2 leading-relaxed' : 'h-11 px-2'} ${invalid ? 'border-ink' : 'border-sand-line focus:border-deep/40'}`;
  const fields: {
    locale: CampaignLocale;
    field: keyof LocalizedCampaignText;
    label: string;
  }[] = [{
    locale: 'pt-BR',
    field: 'title',
    label: 'Título · pt-BR'
  }, {
    locale: 'en-US',
    field: 'title',
    label: 'Title · en-US'
  }, {
    locale: 'pt-BR',
    field: 'message',
    label: 'Mensagem · pt-BR'
  }, {
    locale: 'en-US',
    field: 'message',
    label: 'Message · en-US'
  }];
  return <div className="space-y-4" data-id="element-1858" data-mp="1dbvhcz5quxuz">
      <AdminPageTitle title="Nova comunicação" subtitle="Defina tipo, audiência, idiomas e agendamento. O tipo determina a prioridade no aplicativo." action={<>
            <Button variant="outline" onClick={() => navigate('/admin/notificacoes')} data-id="element-1860" data-mp="g7oxwjksspd">
              Cancelar
            </Button>
            <Button variant="gold" onClick={requestSchedule} aria-disabled={!canSchedule} className={canSchedule ? '' : 'opacity-60'} data-id="element-1861" data-mp="9ejfxzoctsic">
              Agendar envio
            </Button>
          </>} data-id="element-1859" data-mp="1derh8t15jwm2" />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.3fr_1fr]" data-id="element-1862" data-mp="38twsl3y15uu0">
        <div className="space-y-3" data-id="element-1863" data-mp="38vrdii9axmy3">
          <Panel title="Tipo e audiência" data-id="element-1864" data-mp="3ml2z3jzq8sqe">
            <label className="block" data-id="element-1865" data-mp="3mm4m8mu0p5oh">
              <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1866" data-mp="3mn6172e5mcbh">Tipo</span>
              <select value={type} onChange={event => setType(event.target.value as CampaignType)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-1867" data-mp="3hsiacwvoh5tq">
                {Object.entries(typeLabels).map(([id, label]) => <option key={id} value={id} data-id="element-1868" data-mp="313rm2vyuuz2u">
                    {label}
                  </option>)}
              </select>
            </label>

            <label className="mt-3 block" data-id="element-1869" data-mp="2s33bkb8o7gup">
              <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1870" data-mp="2s2abft47vplj">Audiência</span>
              <select value={audience} onChange={event => setAudience(event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-1871" data-mp="2n4ry7qj62o7q">
                <option value="nocheck" data-id="element-1872" data-mp="29dwxj1itndf8">Daily Check ainda não fechado hoje</option>
                <option value="streakProtected" data-id="element-1873" data-mp="24bo3e1u7q5bj">
                  Sequência ativa · com Proteção de Sequência disponível
                </option>
                <option value="streakAtRisk" data-id="element-1874" data-mp="evu75cnra8jh">
                  Sequência em risco · sem Proteção de Sequência disponível
                </option>
                <option value="all" data-id="element-1875" data-mp="jtnazz1l6qgl">Todos os usuários</option>
                <option value="new" data-id="element-1876" data-mp="oub18ihzr39r">Cadastrados nos últimos 7 dias</option>
                <option value="healthSleep" data-id="element-1877" data-mp="os6y42rx28aj">
                  Segmento automático de saúde · sono abaixo de 60%
                </option>
              </select>
            </label>
            <p className="mt-1 text-[11.5px] leading-snug text-muted" data-id="element-1878" data-mp="3scl3hxfmk1yv">
              Avisos de perda de sequência só podem ir para "Sequência em risco · sem Proteção".
              Quem tem Proteção disponível recebe apenas a versão neutra. A segmentação por
              proteção é aplicada pelo backend no envio.
            </p>
            {audience === 'healthSleep' && <div className="mt-3 rounded-card border border-sand-line bg-white p-3" data-id="element-1879" data-mp="39jh6c23776zi">
                <dl className="space-y-1.5 text-[12px]" data-id="element-1880" data-mp="3dksndj80ajw3">
                  <div className="flex justify-between gap-3" data-id="element-1881" data-mp="3djhnhuholwa9">
                    <dt className="text-muted" data-id="element-1882" data-mp="kpstq3evfh4d">Definição</dt>
                    <dd className="text-right text-deep" data-id="element-1883" data-mp="fssmhp6ac8bo">Sono registrado abaixo de 60% da meta nos últimos 7 dias</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-1884" data-mp="ugnc2wozxwkj">
                    <dt className="text-muted" data-id="element-1885" data-mp="phtyz5dmg7kw">Tamanho estimado</dt>
                    <dd className="bee-num font-bold text-deep" data-id="element-1886" data-mp="1shugit8onox">~8.400 usuários</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-1887" data-mp="3sz2fnlwm51e1">
                    <dt className="text-muted" data-id="element-1888" data-mp="3t0n8n73a4c9s">Elegibilidade</dt>
                    <dd className="text-right text-deep" data-id="element-1889" data-mp="au8xth0b4ifm">Só com consentimento e notificações de saúde ativas</dd>
                  </div>
                </dl>
                <p className="mt-2 text-[11.5px] leading-snug text-muted" data-id="element-1890" data-mp="6tyq9zwv9jyn">
                  O Admin vê apenas a definição, o tamanho estimado e a elegibilidade. A lista de
                  usuários e os valores individuais de sono nunca aparecem. O backend calcula a
                  elegibilidade no envio, respeitando consentimento, preferências de notificação,
                  horário de silêncio e limites diários.
                </p>
              </div>}

            <label className="mt-3 block" data-id="element-1891" data-mp="1ftxed31rwo21">
              <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1892" data-mp="1kqntno5r7fme">
                Agendamento
              </span>
              <input value={schedule} onChange={event => setSchedule(event.target.value)} className="bee-num mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-1893" data-mp="28i309t4kcj3g" />
            </label>
          </Panel>

          <Panel title="Conteúdo" hint="Título e mensagem são obrigatórios nos dois idiomas antes de agendar ou enviar." data-id="element-1894" data-mp="23np2xl6cc1w5">
            <div className="space-y-3" data-id="element-1895" data-mp="2w74clrgioee0">
              {fields.map(({
              locale,
              field,
              label
            }) => {
              const id = `campaign-${field}-${locale}`;
              const invalid = fieldError(locale, field);
              return <label key={id} htmlFor={id} className="block" data-id="element-1896" data-mp="13laolna2tilb">
                    <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1897" data-mp="ykd8rhcjh0mz">
                      {label} <span aria-hidden="true" data-id="element-1898" data-mp="tmbfk3lefkxs">*</span>
                    </span>
                    {field === 'title' ? <input id={id} required aria-required="true" aria-invalid={invalid} aria-describedby={invalid ? `${id}-error` : undefined} value={content[locale][field]} onChange={event => update(locale, field, event.target.value)} className={fieldClass(invalid)} data-id="element-1899" data-mp="1raln0y3yf0wa" /> : <textarea id={id} required aria-required="true" aria-invalid={invalid} aria-describedby={invalid ? `${id}-error` : undefined} value={content[locale][field]} onChange={event => update(locale, field, event.target.value)} rows={3} className={fieldClass(invalid, true)} data-id="element-1900" data-mp="2dgxsctl4x06k" />}
                    {invalid && <span id={`${id}-error`} role="alert" className="mt-1 block text-[11.5px] text-ink" data-id="element-1901" data-mp="30f0yj0s61mf4">
                        {locale === 'pt-BR' ? 'Campo obrigatório.' : 'Required field.'}
                      </span>}
                  </label>;
            })}
            </div>
            {attempted && !canSchedule && <p role="alert" className="mt-3 text-[12px] leading-snug text-ink" data-id="element-1902" data-mp="3k65xzprg5tfq">
                Preencha título e mensagem em pt-BR e en-US para agendar ou enviar.
              </p>}
          </Panel>
        </div>

        <div className="space-y-3" data-id="element-1903" data-mp="2pg1jduklq2at">
          <Panel title="Prévia no aplicativo" hint="Como o usuário vê a notificação." data-id="element-1904" data-mp="347k6ecbvox3o">
            <div role="group" aria-label="Idioma da prévia" className="mb-2 inline-flex rounded-pill border border-sand-line bg-white p-[3px]" data-id="element-1905" data-mp="2zgllezfy3au3">
              {(['pt-BR', 'en-US'] as CampaignLocale[]).map(locale => {
              const active = previewLocale === locale;
              return <button key={locale} type="button" aria-pressed={active} onClick={() => setPreviewLocale(locale)} className={`min-h-[32px] rounded-pill px-3 text-[11.5px] font-bold transition-colors duration-150 ease-brand ${active ? 'bg-deep text-sand' : 'text-muted hover:text-deep'}`} data-id="element-1906" data-mp="3n54tpeyzvnm6">
                    {locale.toUpperCase()}
                  </button>;
            })}
            </div>
            <div lang={previewLocale} className={`rounded-card border p-3 ${isHealth ? 'border-gold/45 bg-white' : 'border-sand-line bg-white/80'}`} data-id="element-1907" data-mp="3ppavbg04z1zk">
              <p className={`bee-overline text-[8.5px] font-bold ${isHealth ? 'text-gold-deep' : 'text-muted'}`} data-id="element-1908" data-mp="3gocqwwlscsgg">
                {previewLocale === 'pt-BR' ? typeLabels[type] : typeLabelsEn[type]}
              </p>
              <p className="mt-1 text-[13.5px] font-bold leading-snug text-deep" data-id="element-1909" data-mp="3brcmsr8hxzrz">
                {content[previewLocale].title}
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-muted" data-id="element-1910" data-mp="19z6i5l5zm2na">
                {content[previewLocale].message}
              </p>
            </div>
            <p className="mt-2 text-[11.5px] leading-snug text-muted" data-id="element-1911" data-mp="3sx7yb9nu6b8d">
              {isHealth ? 'Lembretes de saúde têm prioridade visual acima de qualquer aviso social e respeitam as preferências de notificação do usuário.' : 'Comunicações de produto e campanha ficam abaixo dos lembretes de saúde e podem ser desativadas pelo usuário.'}
            </p>
          </Panel>

          <Panel title="Alcance estimado" data-id="element-1912" data-mp="3a8je6px6g9xn">
            <p className="bee-num text-[28px] font-bold leading-none text-deep" data-id="element-1913" data-mp="358ez94bx3n9j">31.208</p>
            <p className="mt-1 text-[11.5px] text-muted" data-id="element-1914" data-mp="316jsvlf4qjow">
              usuários na audiência selecionada, nos dois idiomas
            </p>
          </Panel>
        </div>
      </div>

      <ConfirmationModal open={confirm} title="Agendar esta comunicação?" body={`${typeLabels[type]} para ${audience === 'all' ? 'todos os usuários' : 'a audiência selecionada'}, em ${schedule}. O envio fica registrado na auditoria com o seu nome.`} confirmLabel="Agendar envio" onConfirm={() => {
      if (!canSchedule) return;
      setConfirm(false);
      navigate('/admin/notificacoes');
    }} onClose={() => setConfirm(false)} data-id="element-1915" data-mp="2w9tgp96g488r" />
    </div>;
}